
import 'ol/ol.css';
import { Component, Vue, PropSync, Prop, Watch } from 'vue-property-decorator';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

import QvInput from '@/components/FormComponents/QvInput.vue';
import SearchFarmForm from '@/components/Forms/SearchFarmForm.vue';
import SvgIcon from '@/components/SvgIcon.vue';

import { CommonStore, MapStore, MapStyleStore } from '@/store/modules';

import { Feature, Geolocation, Map, View } from 'ol';

import Draw from 'ol/interaction/Draw';
import Snap from 'ol/interaction/Snap';
import Modify from 'ol/interaction/Modify';

import Point from 'ol/geom/Point';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';

import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import OSM from 'ol/source/OSM';
import BingMaps from 'ol/source/BingMaps';

import Control from 'ol/control/Control';
import ScaleLine from 'ol/control/ScaleLine';
import GeoJSONFormat from 'ol/format/GeoJSON';

import WKT from 'ol/format/WKT';
import { GeoJSONFeature } from 'highcharts';
import XYZ from 'ol/source/XYZ';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import LineString from 'ol/geom/LineString';

import { transform as coTransform } from 'ol/proj';
// import Geocoder from '@/components/Geocoder.vue';

import helpers from '@/utils/helpers';
import Geometry from 'ol/geom/Geometry';
import { Type as GeometryType } from 'ol/geom/Geometry';
import TileImage from 'ol/source/TileImage';
import { Coordinate } from 'ol/coordinate';
import { Extent, intersects } from 'ol/extent';

import { Option } from '@/store/models/common';

@Component({
  name: 'LocationInputMap',
  components: {
    ValidationProvider,
    ValidationObserver,
    QvInput,
    SearchFarmForm,
    SvgIcon
    // Geocoder
  }
})
export default class LocationInputMap extends Vue {
  $refs!: {
    mapForm: InstanceType<typeof HTMLElement>;
    //eslint-disable-next-line
    // geocoder: any;
    gpsImage: InstanceType<typeof HTMLInputElement>;
    polygonCsv: InstanceType<typeof HTMLInputElement>;
    enterCoordinateFormObserver: InstanceType<typeof ValidationObserver>;
  };

  @PropSync('value', { type: [String, Object as () => GeoJSONFeature] })
  syncedLocation!: GeoJSONFeature | string;

  @Prop({ default: 'GeoJSON' })
  // eslint-disable-next-line
  outputType!: string;

  @Prop({ default: '' })
  // eslint-disable-next-line
  qvValidate!: string | Record<string, any>;

  @Prop({ required: false, default: null })
  // eslint-disable-next-line
  defaultCoordinate!: Coordinate | null;

  @Prop({ default: 'Point' })
  // eslint-disable-next-line
  geomType!: GeometryType;

  @Prop({ required: false, default: null })
  // eslint-disable-next-line
  zoomToCoordinate!: Coordinate | null;

  @Prop({ required: false, default: null })
  // eslint-disable-next-line
  highlightCoordinate!: Coordinate | null;

  @Prop({ required: false, default: null })
  // eslint-disable-next-line
  extraLayers!: VectorLayer<any>[] | TileLayer<any>[] | null;

  enterCoordinateDialog = false;
  lat: null | number = null;
  lon: null | number = null;

  searchFarmDialog = false;
  polygonCsvDialog = false;

  view = new View();

  map = new Map({
    target: 'mapForm'
  });

  tempImage: File = {} as File;

  isDrawing = false;
  draw = new Draw({ type: this.geomType });
  snap = new Snap({});
  modify: Modify = {} as Modify;
  drawIcon = 'add';

  geolocation = new Geolocation({
    // enableHighAccuracy must be set to true to have the heading value.
    trackingOptions: {
      enableHighAccuracy: true
    },
    projection: this.view.getProjection()
  });

  positionFeature = new Feature();

  mapSwitcher = false;
  baseLayer = 'Google Satellite';
  baseLayerOptions = [
    {
      label: 'No Base Layer',
      value: 'None'
    },
    {
      label: 'Google Imagery',
      value: 'Google Satellite'
    },
    {
      label: 'OSM',
      value: 'OSM'
    },
    {
      label: 'Bing Imagery',
      value: 'BingMap'
    },
    {
      label: 'Esri Imagery',
      value: 'WorldImagery'
    }
  ];

  highlightCoordinateLayer = new VectorLayer({
    source: new VectorSource({
      features: []
    }),
    style: MapStyleStore.drawBlockHighlightStyle,
    zIndex: 3
  });

  getLayer(layerID: string) {
    const layers = this.map.getLayers().getArray();
    // eslint-disable-next-line
    let l = layers[0] as VectorLayer<any>;
    for (const layer of layers) {
      if (layer.get('id') === layerID) {
        // eslint-disable-next-line
        l = layer as VectorLayer<any>;
        return l;
      }
    }
    return null;
  }

  get drawTooltip() {
    let tooltip = 'Draw';
    if (this.geomType == 'Point') {
      tooltip += ' Point';
    } else if (this.geomType == 'Polygon') {
      tooltip += ' Polygon';
    } else if (this.geomType == 'LineString') {
      tooltip += ' Line';
    }
    return tooltip;
  }

  get isPoint() {
    if (this.geomType == 'Point') {
      return true;
    }
    return false;
  }
  get selectedFarm() {
    return CommonStore.SelectedFarmBlock;
  }

  @Watch('syncedLocation', { deep: true, immediate: true })
  onSyncedLocationChange(
    newVal: GeoJSONFeature | string,
    oldVal: GeoJSONFeature | string
  ) {
    if (newVal) {
      let location: Feature = new Feature({});
      if (typeof newVal == 'string') {
        location = new WKT().readFeature(newVal);
      } else {
        location = new GeoJSONFormat().readFeature(newVal);
      }
      const geometry = location.getGeometry();
      if (location) {
        this.setDraw(location);
      } else {
        this.setDraw();
      }
      let oldLocation = new Feature({});
      if (oldVal) {
        if (typeof oldVal == 'string') {
          oldLocation = new WKT().readFeature(oldVal);
        } else {
          oldLocation = new GeoJSONFormat().readFeature(oldVal);
        }
      }
      const oldGeometry = oldLocation.getGeometry();
      if (
        (oldGeometry &&
          geometry &&
          !intersects(geometry?.getExtent(), oldGeometry?.getExtent())) ||
        (geometry && !oldGeometry)
      ) {
        this.map.getView().fit(geometry.getExtent());
        this.map.getView().setZoom(15);
      }
    }
  }

  @Watch('zoomToCoordinate')
  onZoomToCoordinate(newPosition: Coordinate | Extent | null) {
    if (newPosition) {
      this.map
        .getView()
        .animate({ center: newPosition, zoom: 17, duration: 1000 });
    }
  }

  @Watch('highlightCoordinate')
  onHighlightCoordinate(newPosition: Coordinate | null) {
    if (newPosition) {
      const feature = new Feature();
      feature.setGeometry(new Point(newPosition));
      this.highlightCoordinateLayer.getSource()?.clear();
      this.highlightCoordinateLayer.setSource(
        new VectorSource({
          features: [feature]
        })
      );
      // this.map.addLayer(this.highlightCoordinateLayer)
    } else {
      // this.map.removeLayer(this.highlightCoordinateLayer);
      this.highlightCoordinateLayer.getSource()?.clear();
      this.highlightCoordinateLayer.setSource(new VectorSource());
    }
  }

  setupExtraLayers() {
    if (this.extraLayers) {
      for (const layer of this.extraLayers) {
        const olLayer = this.getLayer(layer.get('id'));
        if (olLayer) {
          this.map.removeLayer(olLayer);
        }
        this.map.addLayer(layer);
      }
    }
  }

  @Watch('extraLayers', { deep: true })
  onExtraLayersChange() {
    this.setupExtraLayers();
  }

  emitInput(geometry: Geometry | undefined) {
    if (geometry) {
      if (this.outputType == 'wkt') {
        const wkt = new WKT().writeGeometry(geometry);
        this.$emit('input', wkt);
      } else {
        const geoJSON = new GeoJSONFormat().writeGeometry(geometry);
        const json = JSON.parse(geoJSON);
        this.$emit('input', json);
        this.onSyncedLocationChange(json, '');
      }
      // Get Area and Length if possible
      this.measureGeometry(geometry);
    }
  }

  async toggleFullscreen() {
    const target = this.$refs.mapForm;
    await this.$q.fullscreen.toggle(target);
    setTimeout(() => {
      this.updateMapSize();
    }, 300);

    setTimeout(() => {
      this.stopUpdateAnimation();
    }, 600);
  }

  get syncedLocationFeature() {
    if (typeof this.syncedLocation == 'string') {
      return new WKT().readFeature(this.syncedLocation);
    }
    return new GeoJSONFormat().readFeature(this.syncedLocation);
  }

  async setDraw(location?: Feature) {
    if (!location) {
      location = this.syncedLocationFeature.clone();
    }

    if (location) {
      const feature = location.clone();
      feature.setStyle(MapStyleStore.drawBlockLocationStyle);
      let vectorLayer = await this.getLayer('LocationLayer');
      const vectorSource = new VectorSource({
        features: [feature]
      });
      if (vectorLayer != null) {
        this.map.removeLayer(vectorLayer);
      }

      vectorLayer = new VectorLayer({
        source: vectorSource,
        zIndex: 2
      });
      vectorLayer.set('id', 'LocationLayer');
      vectorLayer.set('title', 'Location Layer');
      this.map.addLayer(vectorLayer);
      this.map.removeInteraction(this.modify);

      this.modify = new Modify({ source: vectorSource });
      this.modify.on('modifyend', event => {
        const features = event.features.getArray();
        if (features && features.length > 0) {
          const feature = features[0];
          const geometry = feature.getGeometry();
          if (geometry && geometry instanceof Geometry) {
            this.emitInput(geometry);
          }
        }
      });
      this.map.addInteraction(this.modify);
    }
  }

  async addDraw() {
    if (!this.isDrawing) {
      this.$emit('drawStarted');
      this.isDrawing = true;
      const vectorSource = new VectorSource({ features: [] });

      this.draw = new Draw({
        source: vectorSource,
        type: this.geomType,
        style: MapStyleStore.drawBlockLocationStyle
      });

      this.snap = new Snap({
        source: vectorSource
      });

      const vector = await this.getLayer('LocationLayer');
      if (vector == null) {
        const vectorLayer = new VectorLayer({
          source: vectorSource,
          zIndex: 2
        });
        vectorLayer.set('id', 'LocationLayer');
        vectorLayer.set('title', 'Location Layer');
      } else {
        vector.setSource(vectorSource);
        vector.getSource().refresh();
      }
      this.map.removeInteraction(this.draw);
      this.map.removeInteraction(this.snap);
      this.map.addInteraction(this.draw);
      this.map.addInteraction(this.snap);

      this.draw.on('drawend', event => {
        console.log('Draw end');
        this.map.removeInteraction(this.modify);
        this.modify = new Modify({ source: vectorSource });

        this.map.addInteraction(this.modify);
        this.isDrawing = false;
        const feature = event.feature;
        feature.setStyle(MapStyleStore.drawBlockLocationStyle);
        setTimeout(() => {
          this.map.removeInteraction(this.draw);
          this.map.removeInteraction(this.snap);
        }, 1);
        // this.drawIcon = 'edit';
        const geometry = feature.getGeometry();
        // if (geometry) {
        //   this.setDraw();
        //   // this.drawIcon = 'edit';
        //   this.map.getView().fit(geometry.getExtent());
        //   this.map.getView().setZoom(15);
        // }
        this.emitInput(geometry);
      });
    }
  }

  drawUndo() {
    this.draw.removeLastPoint();
  }

  updateDrawStyle() {
    if (
      this.syncedLocation &&
      this.syncedLocationFeature &&
      this.syncedLocationFeature.setStyle
    ) {
      this.syncedLocationFeature.setStyle(MapStyleStore.drawBlockLocationStyle);
    }
  }

  measureGeometry(geometry: Geometry) {
    const transformedGeometry = geometry
      .clone()
      .transform('EPSG:4326', 'EPSG:3857');
    if (
      transformedGeometry instanceof Polygon ||
      transformedGeometry instanceof MultiPolygon
    ) {
      const area = transformedGeometry.getArea();
      const areaSqM = Math.round(area * 100) / 100;
      this.$emit('areaSqM', areaSqM);
    } else if (transformedGeometry instanceof LineString) {
      const length = transformedGeometry.getLength();
      const lengthM = Math.round(length * 100) / 100;
      this.$emit('lengthM', lengthM);
    }
  }

  async switchMap(layer: string) {
    for (const baseLayer of this.baseLayers) {
      baseLayer.setVisible(false);
    }
    if (layer != 'None') {
      const mapLayer = await this.getLayer(layer);
      if (mapLayer != null) {
        mapLayer.setVisible(true);
      }
    }
  }

  useMyGeolocation() {
    this.geolocation.setTracking(false);
    this.geolocation.setTracking(true);
    const position = this.geolocation.getPosition();
    if (position) {
      const pointPosition = new Point(position);
      this.map
        .getView()
        .animate({ center: position, zoom: 15, duration: 1000 });
      this.positionFeature.setGeometry(pointPosition);
      this.positionFeature.setStyle(MapStyleStore.currentLocationStyle);
      this.setDraw(this.positionFeature);
      this.emitInput(pointPosition);
    }

    this.geolocation.setTracking(false);
  }

  getGeolocation() {
    this.geolocation.setTracking(false);
    this.geolocation.setTracking(true);
  }

  async setGeolocation() {
    const position = await this.geolocation.getPosition();
    if (position) {
      this.map.getView().setCenter(position);
      this.positionFeature.setGeometry(new Point(position));
      this.positionFeature.setStyle(MapStyleStore.currentLocationStyle);
    }

    this.geolocation.setTracking(false);
  }

  setupGeolocation() {
    this.positionFeature = new Feature();

    this.positionFeature.setStyle(MapStyleStore.currentLocationStyle);
    const positionLayer = new VectorLayer({
      source: new VectorSource({
        features: [this.positionFeature]
      }),
      zIndex: 1
    });
    positionLayer.set('id', 'Position Layer');
    this.map.addLayer(positionLayer);

    this.geolocation.setTracking(false);
    this.geolocation.setProjection(this.view.getProjection());
    this.geolocation.on('change:position', () => this.setGeolocation());
  }

  openGPSImage() {
    this.$refs.gpsImage.value = '';
    this.$refs.gpsImage.click();
  }

  openPolygonCsv() {
    this.$refs.polygonCsv.value = '';
    this.$refs.polygonCsv.click();
  }

  // eslint-disable-next-line
  async readPolygonFromCsv(e: any) {
    let input!: File;
    // eslint-disable-next-line
    let files: File[] = [];
    if (e instanceof File) {
      input = e;
    } else if (!(e instanceof File) && (e.target || e.dataTransfer)) {
      files = e.target.files || e.dataTransfer.files;
      if (files.length > 0) {
        input = files[0];
      }
    }
    if (input) {
      const reader = new FileReader();
      reader.onload = e => {
        if (e && e.target) {
          const text = e.target.result;
          if (typeof text == 'string') {
            const data = helpers
              .csvToArray(text)
              .sort((a, b) => a.id - b.id)
              .map(x => [Number(x.lon), Number(x.lat)]);
            const first = data[0];
            const last = data[data.length - 1];
            if (first[0] != last[0] || first[1] != last[1]) {
              data.push(data[0]);
            }
            const polygon = new Polygon([data]);
            const extent = polygon.getExtent();
            const feature = new Feature(polygon);

            // Get Area and Length if possible
            this.measureGeometry(polygon);
            this.setDraw(feature);
            this.map.getView().fit(extent);
            this.emitInput(polygon);
            return data;
          }
        }
        return '';
      };

      reader.readAsText(input);
    }
    this.polygonCsvDialog = false;
  }

  // eslint-disable-next-line
  // async readGPSFromImage(e: any) {
  //   const files = e.target.files || e.dataTransfer.files;
  //   if (files.length > 0) {
  //     const gps = await exifr.gps(files[0]);
  //     if (gps) {
  //       this.$emit('input', {
  //         longitude: gps.longitude,
  //         latitude: gps.latitude
  //       });
  //       const position = [gps.longitude, gps.latitude];
  //       const gpsPoint = new Point(position);
  //       this.setDraw(gpsPoint);

  //       this.map
  //         .getView()
  //         .animate({ center: position, zoom: 15, duration: 1000 });
  //     } else {
  //       this.$q.notify({
  //         message: 'Provided image has no gps data',
  //         type: 'negative'
  //       });
  //     }
  //   }
  // }

  // eslint-disable-next-line
  baseLayers: (TileLayer<any> | VectorLayer<any>)[] = [];
  setupBaseLayers() {
    const osmLayer = new TileLayer({
      visible: false,
      source: new OSM()
    });
    osmLayer.set('id', 'OSM');
    const bing = new TileLayer({
      visible: true,
      source: new BingMaps({
        key: 'ApTJzdkyN1DdFKkRAE6QIDtzihNaf6IWJsT-nQ_2eMoO4PN__0Tzhl2-WgJtXFSp',
        imagerySet: 'AerialWithLabels'
      })
    });
    bing.set('id', 'BingMap');

    const worldImagery = new TileLayer({
      visible: false,
      source: new XYZ({
        attributions:
          'Tiles © <a href="https://services.arcgisonline.com/ArcGIS/' +
          'rest/services/World_Imagery/MapServer">ArcGIS</a>',
        url:
          'https://server.arcgisonline.com/ArcGIS/rest/services/' +
          'World_Imagery/MapServer/tile/{z}/{y}/{x}'
      })
    });
    worldImagery.set('id', 'WorldImagery');

    const googleLayerSatellite = new TileLayer({
      visible: true,
      preload: Infinity,
      source: new TileImage({
        wrapX: true,
        projection: 'EPSG:3857',
        attributions:
          'Tiles © <a href="https://maps.google.com">Google Map</a>',
        url: `http://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}`
      })
    });
    googleLayerSatellite.set('id', 'Google Satellite');
    this.baseLayers = [osmLayer, bing, worldImagery, googleLayerSatellite];
  }
  get dynamicBaseLayers() {
    return MapStore.BaseLayers;
  }

  async setupDynamicBaseLayers() {
    await MapStore.getBaseLayers();
    // eslint-disable-next-line
    const baseLayers: TileLayer<any>[] = [];
    const baseLayersOptions: Option[] = [];
    for (const baseLayer of this.dynamicBaseLayers) {
      const layer = new TileLayer({
        source: new TileWMS({
          url: `${baseLayer.url}`,
          params: {
            LAYERS: baseLayer.layer,
            TILED: true,
            HEIGHT: 635,
            WIDTH: 768,
            SRS: baseLayer.srs
          },
          serverType: 'geoserver',
          transition: 0
        }),
        visible: false,
        zIndex: -1
      });
      layer.set('id', baseLayer.layerId);
      baseLayers.push(layer);
      baseLayersOptions.push({
        value: baseLayer.layerId,
        label: baseLayer.layerTitle
      });
    }

    this.baseLayers.push(...baseLayers);
    this.baseLayerOptions.push(...baseLayersOptions);
  }

  setupControls() {
    this.map.addControl(
      new Control({
        element: document.getElementById('drawPanel') as HTMLElement
      })
    );
    this.map.addControl(
      new Control({
        element: document.getElementById('zoomToFarm') as HTMLElement
      })
    );
    this.map.addControl(
      new ScaleLine({
        units: 'metric',
        className: 'location-input-scale-line ol-scale-line'
      })
    );
    this.map.addControl(
      new Control({
        element: document.getElementById(
          'locationInputLayerSwitcher'
        ) as HTMLElement
      })
    );
  }

  async initMap() {
    // await AdminStore.getStaticSettings();
    this.setupBaseLayers();
    await this.setupDynamicBaseLayers();

    const vectorSource = new VectorSource();

    const locationLayer = new VectorLayer({
      source: vectorSource,
      zIndex: 2
    });
    locationLayer.set('id', 'LocationLayer');
    locationLayer.set('title', 'Location Layer');

    const location = [150.5155619, -23.322713];
    const zoom = 6;

    this.view = new View({
      center: location,
      zoom: zoom,
      projection: 'EPSG:4326',
      maxZoom: 21
    });
    this.map = new Map({
      target: this.$refs.mapForm,
      layers: [...this.baseLayers, locationLayer],
      view: this.view
    });

    // this.$refs.geocoder.addControl(this.map);
    this.setZoomToFarm();

    this.setupControls();

    this.setupGeolocation();
    if (this.syncedLocation) {
      const feature = this.syncedLocationFeature;
      const geometry = feature.getGeometry();
      if (geometry) {
        this.setDraw();
        // this.drawIcon = 'edit';
        this.map.getView().fit(geometry.getExtent());
        this.map.getView().setZoom(15);
      }
    } else {
      // this.drawIcon = 'edit';
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!this.syncedLocation) {
        this.getGeolocation();
      } else {
        const feature = this.syncedLocationFeature;
        const geometry = feature.getGeometry();
        if (geometry) {
          this.setDraw();
          // this.drawIcon = 'edit';
          this.map.getView().fit(geometry.getExtent());
          this.map.getView().setZoom(15);
        }
      }
    }
    this.highlightCoordinateLayer.set('id', 'Highlight Coordinate Layer');
    this.map.addLayer(this.highlightCoordinateLayer);
    this.setupExtraLayers();
  }
  requestId = 0;

  updateMapSize() {
    this.map.updateSize();
    this.requestId = requestAnimationFrame(this.updateMapSize);
  }

  stopUpdateAnimation() {
    cancelAnimationFrame(this.requestId);
  }
  zoomToFarm() {
    if (this.defaultCoordinate) {
      this.zoomToPosition(this.defaultCoordinate);
    } else if (this.selectedFarm && this.selectedFarm.extent) {
      this.map.getView().fit(this.selectedFarm.extent);
    } else if (this.selectedFarm && this.selectedFarm.center) {
      const center = this.selectedFarm.center;
      this.zoomToPosition(center);
    }
  }

  zoomToPosition(position: null | Coordinate) {
    if (position) {
      position = coTransform(
        position,
        'EPSG:4326',
        this.map.getView().getProjection()
      );
      this.map.getView().animate({
        center: position,
        zoom: 17,
        duration: 1000
      });
    }
  }

  async setZoomToFarm() {
    await helpers.fetchingDefaultFarm();
    this.zoomToFarm();
  }

  onEnterCoordinate() {
    if (this.lat && this.lon) {
      const pointCoordinate = [Number(this.lon), Number(this.lat)];
      const pointGeoJson = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: pointCoordinate
        }
      };

      const point = new GeoJSONFormat().readFeature(pointGeoJson);
      this.setDraw(point);
      this.emitInput(point.getGeometry());
      const view = this.map.getView();
      view.setCenter([this.lon, this.lat]);
      view.setZoom(15);
    }
    this.lon = null;
    this.lat = null;
    this.enterCoordinateDialog = false;
  }

  onEnterCoordinateReset() {
    this.lon = null;
    this.lat = null;
    this.enterCoordinateDialog = false;
    requestAnimationFrame(() => {
      this.$refs.enterCoordinateFormObserver.reset();
    });
  }

  mounted() {
    MapStore.setOlControlDarkMode(this.$q.dark.isActive);
    this.initMap();
    setTimeout(() => {
      this.updateMapSize();
    }, 300);

    setTimeout(() => {
      this.stopUpdateAnimation();
    }, 600);
    if (this.geomType == 'Point') {
      this.drawIcon = 'fiber_manual_record';
    } else if (this.geomType == 'Polygon') {
      this.drawIcon = 'crop_square';
    } else if (this.geomType == 'LineString') {
      this.drawIcon = 'show_chart';
    }
  }

  beforeDestroy() {
    this.map.dispose();
  }
}
